// src/components/AugustTracklist.js
// August Tracklist — warm gold port of ~/Downloads/tracks.html. Lives inside the Journey tab.
import React, { useEffect, useRef, useState } from 'react'
import { Star, Share2, Check } from 'lucide-react'
import { STATION_NAMES } from '../data/content'
import AugustTexture from './AugustTexture'
import { shareTrackLink } from '../utils/shareLink'
import PageHeader from './ui/PageHeader'
import './AugustTracklist.css'

// Filter pills — Favorites folded in here (design system refresh), replacing the standalone
// Favorites tab. IA change only: favoriteDays/onToggleFavorite data and behavior are unchanged,
// this just changes which screen shows them.
const FILTERS = [
  { id: 'all',       label: 'All' },
  { id: 'sideA',     label: 'Side A' },
  { id: 'sideB',     label: 'Side B' },
  { id: 'favorites', label: '★ Favorites' },
]

// Original lines — safe to port (not the copyrighted reference lyrics used elsewhere).
const WALLPAPER_LINES = [
  "Twenty eight days and each one a little different",
  "Side one ended before I noticed",
  "The needle drops on another morning",
  "Kept the streak alive through the busy week",
  "Read last week back and smiled the whole time",
  "Every track another small proof",
  "What gets written down gets remembered",
  "The quiet ones count the most",
  "One small thank you at a time",
]

const RINGS = [
  { width: 84, height: 84, top: -20, right: 22, opacity: 0.6 },
  { width: 60, height: 60, bottom: 90, left: -16, opacity: 0.45 },
]

const STATIC_SPLATS = [
  { width: 12, height: 10, top: 70, left: 22, opacity: 0.35, borderRadius: '60% 40% 55% 45%' },
  { width: 5,  height: 5,  top: 80, left: 38, opacity: 0.25, borderRadius: '50%' },
  { width: 13, height: 11, bottom: 140, right: 20, opacity: 0.38, borderRadius: '48% 52% 55% 45%' },
]

export default function AugustTracklist({ nextStation = 1, completedCount = 0, onOpenDay, onBack, favoriteDays, onToggleFavorite, augustTheme, onAugustThemeChange }) {
  const reduced = useRef(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  ).current
  const [dotsIn, setDotsIn] = useState(reduced)
  const [justCopied, setJustCopied] = useState(null) // track number whose link was just copied
  const [filter, setFilter] = useState('all')

  useEffect(() => {
    if (reduced) return
    const t = setTimeout(() => setDotsIn(true), 120)
    return () => clearTimeout(t)
  }, [reduced])

  const handleShare = async (n, name) => {
    const result = await shareTrackLink(n, name)
    if (result.copied) {
      setJustCopied(n)
      setTimeout(() => setJustCopied(c => (c === n ? null : c)), 1500)
    }
  }

  // Names read from data, not hardcoded here — future renames in STATION_NAMES.august propagate automatically.
  const names = STATION_NAMES.august || []
  const wallpaper = Array.from({ length: 24 }, (_, i) => WALLPAPER_LINES[i % WALLPAPER_LINES.length])

  const tracks = names.map((name, i) => {
    const n = i + 1
    const status = n <= completedCount ? 'done' : n === nextStation ? 'current' : 'future'
    return { n, name, status }
  })
  const sideA = tracks.slice(0, 14)
  const sideB = tracks.slice(14, 28)
  const favorited = tracks.filter(t => favoriteDays?.has(t.n))

  const renderTrack = (t, globalIdx) => (
    <div
      key={t.n}
      className={`aug-tracklist-track ${t.status}${dotsIn ? ' in' : ''}`}
      style={{ transitionDelay: reduced ? '0ms' : `${globalIdx * 40}ms` }}
      onClick={(t.status === 'done' || t.status === 'current') ? () => onOpenDay(t.n) : undefined}
    >
      <span className="aug-tracklist-num">{t.n}</span>
      <span className="aug-tracklist-name">{t.name}</span>
      <span className="aug-tracklist-mark" />
      {/* Only unlocked tracks are favoritable — a favorited future track would otherwise be
          openable (skipping ahead) from the Favorites tab's quick-access list. */}
      {(t.status === 'done' || t.status === 'current') && (
        <>
          <button
            type="button"
            className={`aug-tracklist-star${favoriteDays?.has(t.n) ? ' active' : ''}`}
            aria-label={favoriteDays?.has(t.n) ? 'Unfavorite track' : 'Favorite track'}
            onClick={(e) => { e.stopPropagation(); onToggleFavorite?.(t.n) }}
          >
            <Star size={15} strokeWidth={1.75} fill={favoriteDays?.has(t.n) ? 'currentColor' : 'none'} />
          </button>
          <button
            type="button"
            className="aug-tracklist-share"
            aria-label="Share track"
            onClick={(e) => { e.stopPropagation(); handleShare(t.n, t.name) }}
          >
            {justCopied === t.n ? <Check size={15} strokeWidth={1.75} /> : <Share2 size={15} strokeWidth={1.75} />}
          </button>
        </>
      )}
    </div>
  )

  return (
    <div className="aug-tracklist">
      <AugustTexture variant="corner" wallpaperLines={WALLPAPER_LINES} rings={RINGS} splats={STATIC_SPLATS} lineCount={24} />

      <div className="aug-tracklist-screen">
        {/* No showBack — Tracklist is a root-level bottom-nav destination, not a child screen
            (final consistency pass: only detail/ritual screens like Gratitude get the back
            chevron). No descriptor either — it duplicated the "All" filter pill's own active
            state right below; removed, not replaced. */}
        <PageHeader
          eyebrow="The Magic • 28 Tracks"
          title="Tracklist"
          augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
        />

        <div className="aug-tracklist-filters">
          {FILTERS.map(f => (
            <button
              key={f.id}
              type="button"
              className={`aug-tracklist-filter-pill${filter === f.id ? ' active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {filter === 'favorites' ? (
          favorited.length === 0 ? (
            <p className="aug-favorites-empty">Tap the star on any track to save it here.</p>
          ) : (
            favorited.map((t, i) => renderTrack(t, i))
          )
        ) : (
          <>
            {(filter === 'all' || filter === 'sideA') && (
              <>
                {filter === 'all' && (
                  <div className="aug-tracklist-side">
                    <span className="aug-tracklist-rule" />
                    <span className="aug-tracklist-side-label">Side A</span>
                    <span className="aug-tracklist-rule" />
                  </div>
                )}
                {sideA.map((t, i) => renderTrack(t, i))}
              </>
            )}

            {(filter === 'all' || filter === 'sideB') && (
              <>
                {filter === 'all' && (
                  <div className="aug-tracklist-side">
                    <span className="aug-tracklist-rule" />
                    <span className="aug-tracklist-side-label">Side B</span>
                    <span className="aug-tracklist-rule" />
                  </div>
                )}
                {sideB.map((t, i) => renderTrack(t, i + 14))}
              </>
            )}
          </>
        )}

        <div className="aug-tracklist-foot">Everything after is up to you</div>
      </div>
    </div>
  )
}
